import { ref } from '@vue/composition-api'
import { findIndex } from 'lodash'
import CustomerApi from '@/api/waiter/Customer'
import { handleApiError } from '@/utils/errorHandler'

export default function useCustomers(vm) {
  const customers = ref([])
  const newCustomerDialog = ref(false)
  const searchQuery = ref('')
  const isSearching = ref(false)
  const pagination = ref({
    page: 1,
    perPage: 50,
    totalCount: 0,
    totalPages: 0,
  })

  const activeFilters = ref({})
  const customersWithStats = ref([])
  const recentCustomers = ref([])
  const upcomingBirthdayCustomers = ref([])

  const getCustomers = async ({ page = 1, perPage = 50, q = '' } = {}) => {
    try {
      const params = { page, perPage }
      if (q) params.q = q

      const res = await CustomerApi.getCustomers(params)

      if (res.data && res.data.data) {
        customers.value = res.data.data || []
        pagination.value = res.data.pagination || {
          page: 1,
          perPage: 50,
          totalCount: 0,
          totalPages: 0,
        }
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })
    }
  }

  // フィルター付き顧客統計一覧を取得
  const getCustomersWithStatsFiltered = async (filters = {}) => {
    isSearching.value = true
    try {
      const params = {
        q: searchQuery.value?.trim() || undefined,
        ...filters,
      }
      const res = await CustomerApi.getCustomersWithStats(params)

      customersWithStats.value = res.data?.customers || []

      // フィルター適用時は統計データを顧客一覧にも反映
      customers.value = customersWithStats.value.map(c => ({
        id: c.id,
        attributes: {
          memberNumber: c.memberNumber,
          name: c.name,
          birthday: c.birthday,
          receiptAddressee: c.receiptAddressee,
          visitCount: c.visitCount,
          lastVisitedAt: c.lastVisitedAt,
          createdAt: c.createdAt,
          customerTags: c.customerTags || [],
          casts: c.casts || [],
        },
      }))
      pagination.value = {
        page: 1,
        perPage: customersWithStats.value.length,
        totalCount: customersWithStats.value.length,
        totalPages: 1,
      }

      // 最近来店した顧客を抽出（最終来店日でソート、上位10件）
      const sorted = [...customersWithStats.value]
        .filter(c => c.lastVisitedAt)
        .sort((a, b) => new Date(b.lastVisitedAt) - new Date(a.lastVisitedAt))
      recentCustomers.value = sorted.slice(0, 10)

      // 30日以内に誕生日の顧客を抽出
      const today = new Date()
      upcomingBirthdayCustomers.value = customersWithStats.value.filter(c => {
        if (!c.birthday) return false
        const bday = new Date(c.birthday)
        const thisYearBday = new Date(today.getFullYear(), bday.getMonth(), bday.getDate())
        let diff = Math.ceil((thisYearBday - today) / (1000 * 60 * 60 * 24))
        if (diff < 0) {
          // 今年の誕生日が過ぎていたら来年で計算
          const nextYearBday = new Date(today.getFullYear() + 1, bday.getMonth(), bday.getDate())
          diff = Math.ceil((nextYearBday - today) / (1000 * 60 * 60 * 24))
        }

        return diff >= 0 && diff <= 30
      })

      return {
        customers: customersWithStats.value,
        summary: res.data?.summary || {},
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return { customers: [], summary: {} }
    } finally {
      isSearching.value = false
    }
  }

  const applyFilters = async filters => {
    activeFilters.value = filters
    await getCustomersWithStatsFiltered(filters)
  }

  const resetFilters = async () => {
    activeFilters.value = {}
    await getCustomersWithStatsFiltered({})
  }

  // API検索（サーバーサイド検索）
  const searchCustomers = async (query, { page = 1, perPage = 50 } = {}) => {
    isSearching.value = true
    try {
      await getCustomers({ page, perPage, q: query?.trim() || '' })
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })
    } finally {
      isSearching.value = false
    }
  }

  const getCustomer = async id => {
    try {
      const res = await CustomerApi.getCustomer(id)

      if (res.data && res.data.data) {
        return res.data.data
      }

      return null
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return null
    }
  }

  const createCustomer = async ({
    name,
    birthday,
    receiptAddressee,
    callback,
  }) => {
    try {
      const res = await CustomerApi.createCustomer({
        name,
        birthday,
        receiptAddressee,
      })

      if (res.data && res.data.data) {
        customers.value = [res.data.data, ...customers.value]
        vm.$toast.success('顧客を新規作成しました')

        return true
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const updateCustomer = async ({
    id,
    name,
    birthday,
    receiptAddressee,
    customer_custom_field_values_attributes,
    callback,
  }) => {
    try {
      const params = {}
      if (name !== undefined) params.name = name
      if (birthday !== undefined) params.birthday = birthday
      if (receiptAddressee !== undefined) params.receiptAddressee = receiptAddressee
      if (customer_custom_field_values_attributes !== undefined) {
        params.customerCustomFieldValuesAttributes = customer_custom_field_values_attributes
      }

      const res = await CustomerApi.updateCustomer(id, params)

      if (res.data && res.data.data) {
        const updatedCustomer = res.data.data
        const customerIdx = findIndex(customers.value, customer => Number(customer.id) === Number(updatedCustomer.id))
        if (customerIdx >= 0) {
          customers.value.splice(customerIdx, 1, updatedCustomer)
        }
        vm.$toast.success('顧客を更新しました')

        return updatedCustomer
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const deleteCustomer = async ({ id, callback }) => {
    try {
      await CustomerApi.deleteCustomer(id)

      const customerIdx = findIndex(customers.value, customer => Number(customer.id) === Number(id))
      if (customerIdx >= 0) {
        customers.value.splice(customerIdx, 1)
      }
      vm.$toast.success('顧客を削除しました')

      return true
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const getVisitHistory = async (id, { page = 1, perPage = 10 } = {}) => {
    try {
      const res = await CustomerApi.getVisitHistory(id, { page, perPage })

      return {
        visitHistory: res.data?.visitHistory || [],
        pagination: res.data?.pagination || {
          page: 1, perPage: 10, totalCount: 0, totalPages: 0,
        },
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {
        visitHistory: [],
        pagination: {
          page: 1, perPage: 10, totalCount: 0, totalPages: 0,
        },
      }
    }
  }

  const getPurchaseHistory = async (id, { page = 1, perPage = 10 } = {}) => {
    try {
      const res = await CustomerApi.getPurchaseHistory(id, { page, perPage })

      return res.data || {}
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {}
    }
  }

  const openNewCustomerDialog = () => {
    newCustomerDialog.value = true
  }

  return {
    customers,
    newCustomerDialog,
    searchQuery,
    isSearching,
    pagination,
    getCustomers,
    getCustomer,
    searchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    getVisitHistory,
    getPurchaseHistory,
    openNewCustomerDialog,
    activeFilters,
    customersWithStats,
    recentCustomers,
    upcomingBirthdayCustomers,
    getCustomersWithStatsFiltered,
    applyFilters,
    resetFilters,
  }
}
